import { Add, Delete, Edit, Search } from "@mui/icons-material";
import {
    Box, Breadcrumbs,
    Button,
    IconButton, MenuItem,
    Paper, Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow, TextField,
    Typography, useMediaQuery, useTheme
} from '@mui/material';
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import api from '../../api';
import { useListFetch } from '../../hooks/useListFetch';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import CustomPagination from "../../components/Pagination";
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZE_OPTIONS } from '../../constants/listPagination';
import { formatCurrencyInCents, formatDate, formatUuid } from '../../helpers/formatters';

function CheckoutsPage() {
    const [checkouts, setCheckouts] = useState([]);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [loadingDelete, setLoadingDelete] = useState(false);
    const navigate = useNavigate(); // Usando useNavigate para redirecionamento
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [currentPage, setCurrentPage] = useState(1); // Página atual
    const [totalPages, setTotalPages] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_LIST_PAGE_SIZE);
    const [searchTerm, setSearchTerm] = useState('');
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchCheckouts = useCallback(() => {
        setIsLoading(true); // Indica que o carregamento está em andamento
        api.get(`checkouts?page=${currentPage}&limit=${itemsPerPage}&q=${searchTerm}`)
            .then(response => {
                setCheckouts(response.data.checkouts);
                setTotalPages(response.data.total_pages);
            })
            .catch(error => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false); // Finaliza o carregamento
            });
    }, [currentPage, itemsPerPage, searchTerm]);

    useListFetch(fetchCheckouts, [currentPage, itemsPerPage, appliedSearchTerm, fetchCheckouts]);

    const handleSearch = () => {
        setCurrentPage(1);
        setAppliedSearchTerm(true);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setCurrentPage(1);
        setAppliedSearchTerm(false);
        setItemsPerPage(DEFAULT_LIST_PAGE_SIZE);
    };

    const handleCreateCheckout = () => {
        navigate('/admin/register-checkout');  // Redireciona para a página de criação de material
    };

    const handleEditCheckout = (id) => {
        navigate(`/admin/edit-checkout/${id}`);  // Redireciona para a página de edição
    };

    const handleDeleteCheckout = (checkoutId) => {
        Swal.fire({
            title: 'Você tem certeza?',
            text: 'Este checkout será removido permanentemente!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: loadingDelete ? '<i class="fas fa-spinner fa-spin"></i> Removendo...' : 'Sim, remover!',
            cancelButtonText: 'Cancelar',
            allowOutsideClick: !loadingDelete, // Impede que o SweetAlert seja fechado durante o carregamento
            preConfirm: () => {
                if (!loadingDelete) {
                    setLoadingDelete(true);
                    return api.delete(`checkouts/${checkoutId}`)
                        .then(response => {
                            setLoadingDelete(false);
                            // Atualiza o estado após remoção
                            setCheckouts(checkouts.filter(checkout => checkout.id !== checkoutId));
                            Swal.fire('Removido!', 'O checkout foi removido com sucesso.', 'success');
                        })
                        .catch(error => {
                            setLoadingDelete(false);
                            console.error('Erro ao remover o checkout:', error);
                            Swal.fire('Erro!', 'Houve um problema ao remover o checkout.', 'error');
                        });
                }
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }
    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Typography color="text.primary">Checkouts</Typography>
            </Breadcrumbs>
            <br />
            <Box sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between',
                alignItems: isMobile ? 'stretch' : 'center',
                marginBottom: 2,
                gap: isMobile ? 2 : 0
            }}>
                {/* Título */}
                <Typography variant="h4" gutterBottom>
                    Checkouts
                </Typography>

                {/* Campo de busca + botões */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: 'center',
                        gap: 2,
                        width: isMobile ? '100%' : 'auto'
                    }}
                >
                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Buscar por cliente"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        fullWidth={isMobile}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<Search />}
                        fullWidth={isMobile}
                    >
                        Buscar
                    </Button>
                    {appliedSearchTerm && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleClearSearch}
                            startIcon={<ClearIcon />}
                            fullWidth={isMobile}
                        >
                            Limpar Busca
                        </Button>
                    )}
                </Box>

                {/* Botão de venda */}
                <Box sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-end', alignItems: 'center' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateCheckout}
                        startIcon={<Add />}
                        fullWidth={isMobile}
                    >
                        Venda
                    </Button>
                </Box>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Produtos</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Canal de venda</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Registrado em</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {Array.isArray(checkouts) && checkouts.length > 0 && checkouts.map((checkout) => (
                            <TableRow key={checkout.id}>
                                <TableCell>{formatUuid(checkout.id)}</TableCell>
                                <TableCell>{checkout.customer?.name || 'N/A'}</TableCell>
                                <TableCell>
                                    {checkout.products && checkout.products.length > 0 && checkout.products.map((product, index) => (
                                        <div key={index}>
                                            - {product.name}: {product.quantity} x {formatCurrencyInCents(product.base_price)}
                                        </div>
                                    ))}
                                </TableCell>
                                <TableCell>{checkout.sales_channel?.name || 'N/A'}</TableCell>
                                <TableCell>{formatCurrencyInCents(checkout.amount)}</TableCell>
                                <TableCell>{formatDate(checkout.created_at)}</TableCell>
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => handleEditCheckout(checkout.id)}
                                        aria-label="editar checkout"
                                    >
                                        <Edit />
                                    </IconButton>
                                    <IconButton
                                        color="error"
                                        onClick={() => handleDeleteCheckout(checkout.id)}
                                        aria-label="remover checkout"
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2, // Espaçamento entre o Select e a paginação
                    mt: 3, // Margem superior para afastar do conteúdo acima
                }}
            >
                <Select
                    value={itemsPerPage}
                    onChange={(event) => {
                        const newLimit = event.target.value;
                        setItemsPerPage(newLimit);
                        setCurrentPage(1);
                    }}
                    variant="standard"
                >
                    {LIST_PAGE_SIZE_OPTIONS.map((size) => (
                        <MenuItem key={size} value={size}>{size} por página</MenuItem>
                    ))}
                </Select>
                <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                        setCurrentPage(page);
                    }}
                />
            </Box>
        </div>
    );
}

export default CheckoutsPage;
